import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ReactComponent as AccountIcon } from '../../../assets/icons/clientProfileRevamped.svg';
import { ReactComponent as RiskToleranceIcon } from '../../../assets/icons/riskToleranceRevamped.svg';
import { ReactComponent as GoalsIcon } from '../../../assets/icons/goalsRevamped.svg';
import { ReactComponent as PlanDetailsIcon } from '../../../assets/icons/planDetailsRevamped.svg';
import { ReactComponent as CompletedIcon } from '../../../assets/icons/stepperActiveCompleted.svg';
import { ReactComponent as ErrorIcon } from '../../../assets/icons/stepperError.svg';
import { ReactComponent as PendingIcon } from '../../../assets/icons/stepperUnchecked.svg';

import {
    WorkflowContainer,
    WorkflowSteps,
    Step,
    StepShape,
    StepContent,
    StepIcon,
    StepText,
    StepStatus
} from '../../../themes/styles/stepper';

interface StepperPillsStep {
    id: string;
    label: string;
    icon: any;
    status: 'completed' | 'active' | 'pending' | 'error';
    content?: React.ReactNode;
    disabled?: boolean;
}

interface StepperPillsProps {
    steps: StepperPillsStep[];
    currentStep?: number;
    className?: string;
    runPlan?: () => void;
    autoUpdateStatus?: boolean;
    /* eslint-disable no-unused-vars */
    onStepClick?: (stepIndex: number) => void;
    setLastStep?: (step: number) => void;
    /* eslint-enable no-unused-vars */
}

export default function StepperPills({
    steps,
    currentStep = 1,
    onStepClick,
    className = '',
    runPlan,
    setLastStep,
    autoUpdateStatus = true
}: StepperPillsProps) {
    const theme = useTheme();
    const { t } = useTranslation();
    const defaultSteps: StepperPillsStep[] = [
        {
            id: 'profile',
            label: t('CLIENT_ONBOARDING_STEP1'),
            icon: <AccountIcon />,
            status: 'completed'
        },
        {
            id: 'risk',
            label: t('CLIENT_PROFILE_RISK_TOLERANCE_HEADING'),
            icon: <RiskToleranceIcon />,
            status: 'pending'
        },
        {
            id: 'goals',
            label: t('CLIENT_ONBOARDING_STEP2'),
            icon: <GoalsIcon />,
            status: 'pending'
        },
        {
            id: 'plan',
            label: t('CLIENT_ONBOARDING_STEP4'),
            icon: <PlanDetailsIcon />,
            status: 'pending'
        }
    ];

    const activeSteps = steps || defaultSteps;
    const [activeStep, setActiveStep] = useState(currentStep);

    useEffect(() => {
        setActiveStep(currentStep);
    }, [currentStep]);

    useEffect(() => {
        setLastStep?.(activeStep);
    }, [activeStep, setLastStep]);

    const handleStepClick = (stepIndex: number) => {
        const step = stepsWithStatus[stepIndex];
        // Allow clicking on disabled steps if they are in error state (for client profile validation issues)
        if (step.disabled && step.status !== 'error') {
            return;
        }
        if (!onStepClick) setActiveStep(stepIndex);
        onStepClick?.(stepIndex);
    };

    const goToNextStep = () => {
        if (activeStep < activeSteps.length - 1) {
            const nextStep = activeStep + 1;
            if (!onStepClick) setActiveStep(nextStep);
            onStepClick?.(nextStep);
        }
    };

    const goToPreviousStep = () => {
        if (activeStep > 0) {
            const prevStep = activeStep - 1;
            if (!onStepClick) setActiveStep(prevStep);
            onStepClick?.(prevStep);
        }
    };

    const goToStep = (stepIndex: number) => {
        if (stepIndex >= 0 && stepIndex < activeSteps.length) {
            if (!onStepClick) setActiveStep(stepIndex);
            onStepClick?.(stepIndex);
        }
    };

    const isFirstStep = activeStep === 0;
    const isLastStep = activeStep === activeSteps.length - 1;

    const getStepStatus = (index: number): 'completed' | 'active' | 'pending' | 'error' => {
        if (activeSteps[index]?.status === 'error') return 'error';
        // If this is the active step but not completed, return 'pending'
        if (index === activeStep && activeSteps[index]?.status !== 'completed') return 'pending';
        if (index === activeStep) return 'active';
        // all other status is handled from parent component
        if (activeSteps[index]?.status) return activeSteps[index]?.status;

        return 'pending';
    };

    // Derive the step list during render instead of copying the steps prop into state via an
    // effect. The old useState + useEffect(setStepsWithStatus) re-ran on every parent render (the
    // steps prop is a new array each time), forcing a second render of the stepper - which made the
    // active step's content (e.g. PlanExecutionStep) render twice per parent update.
    const stepsWithStatus = autoUpdateStatus
        ? activeSteps.map((step, index) => ({
              ...step,
              status: getStepStatus(index) as 'completed' | 'active' | 'pending' | 'error'
          }))
        : activeSteps;

    const getStepPath = (index: number, isLast: boolean) => {
        if (isLast) {
            return 'M30,30 L-4,2 L315,2 L315,58 L-4,58 Z';
        }
        if (index === 0) {
            return 'M2,2 L280,2 315,30 L280,58 L2,58 Z';
        }
        return 'M30,30 L-4,2 L280,2 315,30 L280,58 L-4,58 Z';
    };

    const getStepColors = (status: string) => {
        if (status === 'active') {
            return {
                fill: theme.palette.semantic.bg.brandDark,
                stroke: theme.palette.semantic.bg.brandDark
            };
        }
        if (status === 'error') {
            return { fill: theme.palette.semantic.bg.error, stroke: theme.palette.semantic.bd.error };
        }
        return { fill: theme.palette.semantic.bg.brandLight2, stroke: theme.palette.semantic.bg.brandLight2 };
    };

    const getCurrentStepContent = () => {
        const currentStepData = stepsWithStatus[activeStep];
        if (currentStepData?.content) {
            if (React.isValidElement(currentStepData.content)) {
                return React.cloneElement(currentStepData.content as React.ReactElement<any>, {
                    updateStep: setActiveStep,
                    goToNextStep,
                    goToPreviousStep,
                    goToStep,
                    currentStep: activeStep,
                    isFirstStep,
                    isLastStep,
                    runPlan
                });
            }
            return currentStepData.content;
        }
        return null;
    };

    const getLineColor = (step: StepperPillsStep) => {
        if (step.status === 'active') {
            return theme.palette.semantic.bd.brand;
        }
        if (step.status === 'error') {
            return theme.palette.semantic.bd.error;
        }
        if (step.disabled) {
            return theme.palette.semantic.bd.strong;
        }

        return theme.palette.semantic.bd.brand;
    };

    return (
        <WorkflowContainer className={className}>
            <WorkflowSteps>
                {stepsWithStatus.map((step, index) => {
                    const status = autoUpdateStatus ? getStepStatus(index) : step.status;
                    const colors = getStepColors(status);
                    const isLast = index === stepsWithStatus.length - 1;
                    const isFirst = index === 0;
                    // Check if step is active but not completed (pending active state)
                    const isActiveButNotCompleted = index === activeStep && activeSteps[index]?.status !== 'completed';
                    const isError = activeSteps[index]?.status === 'error';

                    return (
                        <Step
                            key={step.id}
                            $isFirst={isFirst}
                            $isLast={isLast}
                            $status={status}
                            $disabled={step.disabled}
                            $isPendingActive={isActiveButNotCompleted}
                            $isError={isError}
                            $stepCount={stepsWithStatus.length}
                            onClick={() => handleStepClick(index)}
                            $stepIndex={index}
                        >
                            <StepShape className="step-shape">
                                <svg
                                    viewBox="0 0 300 60"
                                    preserveAspectRatio="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d={getStepPath(index, isLast)}
                                        fill={colors.fill}
                                        stroke={colors.stroke}
                                        strokeWidth={2}
                                    />
                                    {!isFirst && (
                                        <>
                                            {' '}
                                            <line
                                                x1="-4"
                                                y1="2"
                                                x2="30"
                                                y2="30"
                                                stroke={colors.fill}
                                                strokeWidth="0"
                                                strokeLinecap="square"
                                                vectorEffect="non-scaling-stroke"
                                                shapeRendering="geometricPrecision"
                                            />
                                            <line
                                                x1="-4"
                                                y1="58"
                                                x2="30"
                                                y2="30"
                                                stroke={colors.fill}
                                                strokeWidth="0"
                                                strokeLinecap="square"
                                                vectorEffect="non-scaling-stroke"
                                                shapeRendering="geometricPrecision"
                                            />
                                        </>
                                    )}

                                    {!isLast &&
                                        activeSteps[index + 1]?.status !== 'error' &&
                                        activeStep !== index && (
                                            <>
                                                <line
                                                    x1="280"
                                                    y1="2"
                                                    x2="315"
                                                    y2="30"
                                                    stroke={getLineColor(step)}
                                                    strokeWidth="2"
                                                    strokeLinecap="square"
                                                    vectorEffect="non-scaling-stroke"
                                                    shapeRendering="geometricPrecision"
                                                />
                                                <line
                                                    x1="280"
                                                    y1="58"
                                                    x2="315"
                                                    y2="30"
                                                    stroke={getLineColor(step)}
                                                    strokeWidth="2"
                                                    strokeLinecap="square"
                                                    vectorEffect="non-scaling-stroke"
                                                    shapeRendering="geometricPrecision"
                                                />
                                            </>
                                        )}
                                </svg>
                            </StepShape>
                            <StepContent $isFirst={isFirst} $isLast={isLast}>
                                <StepIcon className="step-icon">{step.icon}</StepIcon>
                                <StepText $isFirst={isFirst} className="step-text">
                                    {step.label}
                                </StepText>
                                <StepStatus className={`step-status ${status}`}>
                                    {status === 'completed' || status === 'active' ? <CompletedIcon /> : ''}
                                    {status === 'error' ? <ErrorIcon /> : ''}
                                    {status === 'pending' ? <PendingIcon color={colors.stroke} /> : ''}
                                </StepStatus>
                            </StepContent>
                        </Step>
                    );
                })}
            </WorkflowSteps>

            {getCurrentStepContent() && <div className="step-content-area">{getCurrentStepContent()}</div>}
        </WorkflowContainer>
    );
}

export type { StepperPillsStep, StepperPillsProps };
export { StepperPills };
